import cytoscape from 'cytoscape'
import fcose from 'cytoscape-fcose'
import { useEffect, useRef } from 'react'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import type { GraphPreviewEdge, GraphPreviewNode } from '@/services/api/playground'

if (typeof cytoscape('core', 'fcose') === 'undefined') {
  cytoscape.use(fcose)
}

function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

interface GraphPreviewProps {
  nodes: GraphPreviewNode[]
  edges: GraphPreviewEdge[]
  /** Tailwind height class for the canvas (default `h-80`). */
  heightClass?: string
}

/** Minimal, theme-aware Cytoscape preview for the demo. Node fill/stroke come
 *  from the shared per-type `LABEL_COLORS` map (same identity as the graph
 *  canvas), and chrome colors are read from CSS theme variables. */
export function GraphPreview({ nodes, edges, heightClass = 'h-80' }: GraphPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container || nodes.length === 0) return

    // Cytoscape styles take literal color strings, not Tailwind tokens — read the
    // theme var first and only fall back to a literal if it's unset (frontend-rules-ok).
    const fallbackFill = cssVar('--primary', '#b87333') // frontend-rules-ok
    const foreground = cssVar('--foreground', '#e8e8ee') // frontend-rules-ok
    const border = cssVar('--border', 'rgba(255,255,255,0.2)') // frontend-rules-ok
    const muted = cssVar('--muted-foreground', '#8a8a96') // frontend-rules-ok
    const cardBg = cssVar('--card', '#161619') // frontend-rules-ok

    const nodeIds = new Set<string>()
    const nodeElements = nodes
      .filter(n => {
        if (!n.id || nodeIds.has(n.id)) return false
        nodeIds.add(n.id)
        return true
      })
      .map(n => {
        // Per-type color so each entity type is visually distinct (matches the cards).
        const color = LABEL_COLORS[n.type ?? ''] ?? LABEL_COLORS.Default
        return {
          data: {
            id: n.id,
            label: n.label || n.id,
            bg: color?.fill ?? fallbackFill,
            bd: color?.stroke ?? border,
          },
        }
      })

    const edgeElements = edges
      .filter(e => e.source && e.target && nodeIds.has(e.source) && nodeIds.has(e.target))
      .map(e => ({ data: { id: e.id, source: e.source, target: e.target, label: e.label ?? '' } }))

    const cy = cytoscape({
      container,
      elements: [...nodeElements, ...edgeElements],
      style: [
        {
          selector: 'node',
          style: {
            'background-color': 'data(bg)',
            'border-color': 'data(bd)',
            'border-width': 1.5,
            label: 'data(label)',
            color: foreground,
            'font-size': 9,
            'text-valign': 'bottom',
            'text-halign': 'center',
            'text-margin-y': 4,
            'text-max-width': '120px',
            'text-wrap': 'ellipsis',
            width: 22,
            height: 22,
          },
        },
        {
          selector: 'edge',
          style: {
            width: 1.5,
            'line-color': muted,
            'target-arrow-color': muted,
            'target-arrow-shape': 'triangle',
            'arrow-scale': 0.85,
            'curve-style': 'bezier',
            label: 'data(label)',
            'font-size': 8,
            color: foreground,
            'text-rotation': 'autorotate',
            // Readable chip behind the label so it doesn't blend into the edge.
            'text-background-color': cardBg,
            'text-background-opacity': 0.9,
            'text-background-padding': '2px',
            'text-background-shape': 'roundrectangle',
          },
        },
      ],
      layout: { name: 'fcose', randomize: true, padding: 20 } as cytoscape.LayoutOptions,
      minZoom: 0.2,
      maxZoom: 2.5,
    })

    return () => {
      cy.destroy()
    }
  }, [nodes, edges])

  if (nodes.length === 0) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-md border border-border bg-card text-sm text-muted-foreground ${heightClass}`}
      >
        No entities extracted
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`w-full rounded-md border border-border bg-card ${heightClass}`}
    />
  )
}
